'use client';

import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import videojs from 'video.js';

type Props = {
	options: videojs.PlayerOptions;
	className?: string;
	pause?: boolean;
};

export const VideoJsPlayer = ({ options, pause, className }: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const playerRef = useRef<videojs.Player | null>(null);

	useEffect(() => {
		// @ts-expect-error no needs to check the module type
		import('video.js/dist/video-js.css');
		// @ts-expect-error no needs to check the module type
		import('../../../assets/css/components/video-player.css');
	}, []);

	useEffect(() => {
		if (!playerRef.current && containerRef.current) {
			// This flag prevents video.js from adding dynamic styles for each video.
			// Howerver, it will be ignored once since the module is executed before setting it here.
			// In this case, default styles containing `padding-top: 56.25%` are injected.
			// This is exactly what we need.
			(window as any).VIDEOJS_NO_DYNAMIC_STYLE = true;

			const videoElement = containerRef.current.appendChild(document.createElement('video-js'));
			videoElement.classList.add('vjs-big-play-centered');
			playerRef.current = videojs(videoElement, options);
		}
	}, [options, playerRef, containerRef]);

	useEffect(() => {
		if (options.autoplay && playerRef?.current) {
			playerRef.current?.bigPlayButton?.hide();
		}
	}, [options]);

	useEffect(() => {
		if (pause) {
			playerRef.current?.pause();
		}
	}, [pause, playerRef]);

	useEffect(() => {
		const player = playerRef.current;

		return () => {
			if (player) {
				player.dispose();
				playerRef.current = null;
			}
		};
	}, [options.autoplay, playerRef]);

	return (
		<div
			ref={containerRef}
			className={classNames(
				'absolute! left-0 top-0 w-full h-full [&_.video-js]:pt-[56.25%]!',
				className,
			)}
		/>
	);
};

VideoJsPlayer.displayName = 'VideoJsPlayer';

export default VideoJsPlayer;
